import { LogOut, ShieldCheck, UsersRound } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { ApiError, getMe } from "../services/api";
import { initializeTeamsIfNeeded, type HostMode, type TeamsHostState } from "../teams/teamsContext";
import type { MeResponse } from "../types/peopleAdmin";

interface AppShellProps {
  hostMode: HostMode;
  children: (me: MeResponse) => ReactNode;
}

export default function AppShell({ hostMode, children }: AppShellProps) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [teamsState, setTeamsState] = useState<TeamsHostState>({ inTeams: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauthenticated, setUnauthenticated] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function boot() {
      try {
        const [identity, teams] = await Promise.all([getMe(), initializeTeamsIfNeeded(hostMode)]);
        if (!mounted) {
          return;
        }

        setMe(identity);
        setTeamsState(teams);
        setError(null);
        setUnauthenticated(false);
      } catch (bootError) {
        if (!mounted) {
          return;
        }

        if (bootError instanceof ApiError && bootError.status === 401) {
          setUnauthenticated(true);
          setError(null);
        } else {
          setError(bootError instanceof Error ? bootError.message : "Could not start People Admin.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    boot();
    return () => {
      mounted = false;
    };
  }, [hostMode]);

  const modeLabel = teamsState.inTeams ? "Teams" : hostMode === "sharepoint" ? "SharePoint" : "Web";

  return (
    <div className={`app-shell app-shell-${hostMode}`}>
      <header className="topbar">
        <div className="brand-lockup">
          <div className="brand-mark" aria-hidden="true">
            <UsersRound size={22} />
          </div>
          <div>
            <h1>People Admin</h1>
            <p>{modeLabel}</p>
          </div>
        </div>
        {me ? (
          <div className="identity-block">
            <ShieldCheck size={18} aria-hidden="true" />
            <span>{me.user.displayName || me.user.userPrincipalName}</span>
            {me.user.identityProvider === "development" ? (
              <span className="dev-mode-pill">Local dev</span>
            ) : (
              <a className="icon-link" href="/.auth/logout?post_logout_redirect_uri=/" title="Sign out">
                <LogOut size={18} aria-hidden="true" />
                <span>Sign out</span>
              </a>
            )}
          </div>
        ) : null}
      </header>

      {loading ? <main className="status-surface">Loading People Admin...</main> : null}

      {!loading && unauthenticated ? (
        <main className="status-surface">
          <div className="status-panel">
            <h2>Sign in required</h2>
            <a className="primary-button" href="/.auth/login/aad?post_login_redirect_uri=/">
              Sign in with Microsoft
            </a>
          </div>
        </main>
      ) : null}

      {!loading && error ? (
        <main className="status-surface">
          <div className="status-panel">
            <h2>Access unavailable</h2>
            <p>{error}</p>
          </div>
        </main>
      ) : null}

      {!loading && me && !error ? <main className="workspace">{children(me)}</main> : null}
    </div>
  );
}
